import { Button, Input, Transfer } from 'antd';
import type { TransferItem } from 'antd/es/transfer';
import React, { useEffect, useMemo, useState } from 'react';

import Modal from '../../foundation/components/modal/Modal';
import {
  useGetMedicalInvestigationsForVariantQuery,
  useUpdateReportVariantMutation,
} from '../api/variantApi';
import type { LinkedVariant } from '../probands_samples/types';
import type { SampleDetail } from './types';

type SampleDetailDiagnosticPanelModalProps = {
  open: boolean;
  sample?: SampleDetail;
  variant?: LinkedVariant;
  onClose: () => void;
};

const SampleDetailDiagnosticPanelModal = ({
  open,
  sample,
  variant,
  onClose,
}: SampleDetailDiagnosticPanelModalProps) => {
  const [updateReportVariant, { isLoading }] = useUpdateReportVariantMutation();
  const { data: medicalInvestigations } =
    useGetMedicalInvestigationsForVariantQuery(
      { variant_id: variant?.variant_id as number },
      { skip: !variant?.variant_id },
    );

  const panelOptions: TransferItem[] = useMemo(
    () =>
      (medicalInvestigations ?? []).map(
        (investigation: {
          id: number;
          external_id?: number | null;
          name?: string | null;
        }) => ({
          key: String(investigation.id),
          title:
            investigation.name?.trim() ||
            (investigation.external_id != null
              ? String(investigation.external_id)
              : `Panel ${investigation.id}`),
        }),
      ),
    [medicalInvestigations],
  );

  const [selectedPanelKeys, setSelectedPanelKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedPanelKeys(
      (variant?.report_data ?? []).map((reportData) =>
        String(reportData.medicalinvestigation_id),
      ),
    );
  }, [open, variant?.report_data]);

  const handleSave = async () => {
    if (!sample || !variant?.genotype_id || !variant.variant_id) {
      return;
    }

    await updateReportVariant({
      sample_id: String(sample.id),
      genotype_id: String(variant.genotype_id),
      requestData: selectedPanelKeys,
      variant_id: variant.variant_id,
    }).unwrap();
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Please select a diagnostic panel for the sample"
      onCancel={onClose}
      footer={null}
      width={760}
      className="sample-detail-diagnostic-panel-modal"
    >
      <div className="sample-detail-diagnostic-panel-modal__field">
        <div className="sample-detail-diagnostic-panel-modal__label">
          Sample ID:
        </div>
        <Input value={sample?.sample_id ?? ''} disabled />
      </div>

      <div className="sample-detail-diagnostic-panel-modal__field">
        <div className="sample-detail-diagnostic-panel-modal__label">
          Selected diagnostic panel for report:
        </div>
        <Transfer
          className="sample-detail-diagnostic-panel-modal__transfer"
          dataSource={panelOptions}
          targetKeys={selectedPanelKeys}
          onChange={(nextTargetKeys) =>
            setSelectedPanelKeys(nextTargetKeys as string[])
          }
          render={(item) => item.title || ''}
          showSearch
          locale={{
            searchPlaceholder: 'Enter search term to filter',
            itemUnit: 'panel',
            itemsUnit: 'panels',
          }}
          filterOption={(inputValue, item) =>
            (item.title || '').toLowerCase().includes(inputValue.toLowerCase())
          }
        />
      </div>

      <div className="sample-detail-diagnostic-panel-modal__actions">
        <Button
          type="primary"
          loading={isLoading}
          disabled={!sample || !variant?.genotype_id || !variant.variant_id}
          onClick={handleSave}
        >
          Save changes
        </Button>
      </div>
    </Modal>
  );
};

export default SampleDetailDiagnosticPanelModal;
